<script setup>
import {
  computed,
} from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useForm, useField } from 'vee-validate'
import {
  KkBox,
  KkFormLabel,
  KkSelect,
  KkTextField,
  KkTextarea,
  KkValidationError,
} from '../../../base'
import {
  KkInfo
} from '../../../ui'
import {
  KkFacebookPreview,
  KkInstagramPreview,
  KkTwitterPreview,
  KkPostMedia,
  KkSelectAccounts,
  KkPostSetting
} from '../../../../components/domains/sns'
import iconInstagramPath from '../../../../../../images/sns/icon_instagram.svg'
import iconFacebookPath from '../../../../../../images/sns/icon_facebook.svg'
import iconTwitterPath from '../../../../../../images/sns/icon_x.svg'
const MB = 1000 * 1000
const INSTAGRAM_IMAGE_MIN_ASPECT_RATIO = 1 / 1.91
const INSTAGRAM_IMAGE_MAX_ASPECT_RATIO = 5 / 4
const INSTAGRAM_MOVIE_MIN_ASPECT_RATIO = 4 / 5
const INSTAGRAM_MOVIE_MAX_ASPECT_RATIO = 16 / 9
const INSTAGRAM_MOVIE_MAX_WIDTH = 1920
const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const snsPostsLoading = computed(() => store.getters['companiesStoreSnsPosts/loading'])
const selectedPostSnsType = computed(() => store.getters['companiesStoreSnsPosts/selectedPostSnsType'])
const categories = computed(() => store.getters['companiesSnsCategories/categories'])
const post = computed(() => store.getters['companiesStoreSnsPosts/post'])
const isSelectedAccounts = computed(() => post.value.facebookPages.length !== 0 || post.value.instagramBusinessAccounts.length !== 0 || post.value.twitterUserProfiles.length !== 0)
const readSnsCategories = () => store.dispatch('companiesSnsCategories/readSnsCategories')
const updatePostTitle = (payload) => store.dispatch('companiesStoreSnsPosts/updatePostTitle', payload)
const updatePostCategoryId = (payload) => store.dispatch('companiesStoreSnsPosts/updatePostCategoryId', payload)
const updatePostText = (payload) => store.dispatch('companiesStoreSnsPosts/updatePostText', payload)
const updateImages = (payload) => store.dispatch('companiesStoreSnsPosts/updateImages', payload)
const updateMovie = (payload) => store.dispatch('companiesStoreSnsPosts/updateMovie', payload)
const loading = computed(() => snsPostsLoading.value)

const props = defineProps({
  needApproval: {
    type: Boolean,
    default: false
  },
  isPostEdit: {
    type: Boolean,
    required: true
  },
})
const selectAccountSns = computed(() => {
  const result = []
  if(selectedPostSnsType.value !== 'MULTI'){
    result.push(selectedPostSnsType.value)
    return result
  }

  if(post.value.facebookPages.length !== 0) result.push('FACEBOOK')
  if(post.value.instagramBusinessAccounts.length !== 0) result.push('INSTAGRAM')
  if(post.value.twitterUserProfiles.length !== 0) result.push('TWITTER')
  return result
})
const postImageNumLimit = computed(() => {
  const values = {'FACEBOOK': 80, 'INSTAGRAM': 10, 'TWITTER': 4 }
  const results = selectAccountSns.value.map(v => values[v])
  return Math.min(...results)
})
const imageSizeLimit = computed(() => {
  const values = {'FACEBOOK': MB * 10, 'INSTAGRAM': MB * 8, 'TWITTER': MB * 5 }
  const results = selectAccountSns.value.map(v => values[v])
  return Math.min(...results)
})
const allowedImageAspectRatio = computed(() => {
  if(!selectAccountSns.value.includes('INSTAGRAM')) return null

  return  { min: INSTAGRAM_IMAGE_MIN_ASPECT_RATIO, max: INSTAGRAM_IMAGE_MAX_ASPECT_RATIO }
})
const allowedImageMimes = computed(() => {
  const values = {
    'FACEBOOK': ['image/jpeg', 'image/png', 'image/bmp', 'image/gif',  'image/tiff'],
    'INSTAGRAM': ['image/jpeg', 'image/png'],
    'TWITTER': ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  }
  let results = []
  selectAccountSns.value.forEach((v, i) => {
    if(i === 0) {
      results = values[v]
      return
    }
    var s = new Set(results)
    results = values[v].filter(item => s.has(item))
  })
  return results
})
const movieSizeLimit = computed(() => {
  const values = {'FACEBOOK': 0, 'INSTAGRAM': MB * 100, 'TWITTER': MB * 100 }
  const results = selectAccountSns.value.map(v => values[v])
  return Math.min(...results)
})
const allowedMovieAspectRatio = computed(() => {
  if(!selectAccountSns.value.includes('INSTAGRAM')) return null

  return  { min: INSTAGRAM_MOVIE_MIN_ASPECT_RATIO, max: INSTAGRAM_MOVIE_MAX_ASPECT_RATIO }
})
const allowedMovieMimes = computed(() => {
  const values = {
    'FACEBOOK': [],
    'INSTAGRAM': ['video/quicktime', 'video/mp4'],
    'TWITTER': ['video/mp4']
  }
  let results = []
  selectAccountSns.value.forEach((v, i) => {
    if(i === 0) {
      results = values[v]
      return
    }
    var s = new Set(results)
    results = values[v].filter(item => s.has(item))
  })
  return results
})
const textCharacterLimit = computed(() => {
  const values = {'FACEBOOK': 60000, 'INSTAGRAM': 2200, 'TWITTER': 140 }
  const results = selectAccountSns.value.map(v => values[v])
  return Math.min(...results)
})
const hashtagLimit = computed(() => {
  const values = {'FACEBOOK': 30, 'INSTAGRAM': 30, 'TWITTER': 140 }
  const results = selectAccountSns.value.map(v => values[v])
  return Math.min(...results)
})
const atSignLimit = computed(() => {
  // instagramのみ、上限20個であることがはっきりとわかっている。それ以外に関しては情報が無さそうなので各SNS文字数上限を上限としている。
  const values = {'FACEBOOK': 60000, 'INSTAGRAM': 20, 'TWITTER': 140 }
  const results = selectAccountSns.value.map(v => values[v])
  return Math.min(...results)
})
const allowedMovieTime = (time) => {
  let result = false
  if(selectAccountSns.value.includes('TWITTER')) result = 0.5 <= time && time <= 140
  if(selectAccountSns.value.includes('INSTAGRAM')) result = 3 <= time && time <= 60
  return result
}
const schema = computed(() => {
  return {
    title: { required: true, max: [40] },
    categoryId: '',
    text: { max: [textCharacterLimit.value], maxHashtag: [hashtagLimit.value], maxAtSign: [atSignLimit.value] },
  }
})

const { handleSubmit, meta } = useForm({
  validationSchema: schema,
  initialValues: {
    categoryId: post.value.categoryId,
    title: post.value.title,
    text: post.value.text,
  },
})
const { value: title, errorMessage: titleError, handleChange: titleHandleChange} = useField('title', undefined, { label: t('pages.CompaniesStoreSnsPost.form.postTitle') })
const { value: categoryId, errorMessage: categoryIdError, handleChange: categoryIdHandleChange} = useField('categoryId', undefined, { label: t('pages.CompaniesStoreSnsPost.form.category') })
const { value: text, errorMessage: textError, handleChange: textHandleChange } = useField('text', undefined, { label: t('pages.CompaniesStoreSnsPost.form.text') })

const postTitle = computed(() => {
  switch(selectedPostSnsType.value) {
  case 'MULTI':
    return t('models.sns.postSnsType.multi')
  case 'FACEBOOK':
    return t('models.sns.postSnsType.facebook')
  case 'INSTAGRAM':
    return t('models.sns.postSnsType.instagram')
  case 'TWITTER':
    return t('models.sns.postSnsType.twitter')
  default:
    return ''
  }
})

const snsImage = computed(() => {
  switch(selectedPostSnsType.value) {
  case 'MULTI':
    return ''
  case 'FACEBOOK':
    return iconFacebookPath
  case 'INSTAGRAM':
    return iconInstagramPath
  case 'TWITTER':
    return iconTwitterPath
  default:
    return ''
  }
})

const categoryOptions = () => {
  const results = {}
  results[t('labels.notSet')] = ''
  categories.value.forEach(category => results[category.name] = category.id)
  return results
}

const mediaDisabled = computed(() => {
  return loading.value || post.value.images.length >= postImageNumLimit.value || post.value.movie != null
})

const showFacebookPreview = computed(() => {
  if (selectedPostSnsType.value === 'MULTI' || selectedPostSnsType.value === 'FACEBOOK') {
    return post.value.facebookPages.length
  }
  return false
})

const showInstagramPreview = computed(() => {
  if (selectedPostSnsType.value === 'MULTI' || selectedPostSnsType.value === 'INSTAGRAM') {
    return post.value.instagramBusinessAccounts.length
  }
  return false
})

const showTwitterPreview = computed(() => {
  if (selectedPostSnsType.value === 'MULTI' || selectedPostSnsType.value === 'TWITTER') {
    return post.value.twitterUserProfiles.length
  }
  return false
})

const mediaNote = computed(() => {
  if (selectAccountSns.value.includes('FACEBOOK')) {
    return t('pages.CompaniesStoreSnsPost.form.notUploadMovieToFacebook')
  }
  return t('pages.CompaniesStoreSnsPost.form.mediaNote')
})

const emit = defineEmits(['open-action-modal', 'open-accounts-modal'])

const openActionModal = handleSubmit(async (value) => {
  updatePostCategoryId(value.categoryId)
  updatePostTitle(value.title)
  updatePostText(value.text)

  if (!meta.value.valid) {
    return alert(t('messages.invalid'))
  }
  emit('open-action-modal')
})

const handleImagesChange = async($event) => {
  const image = $event.target.files[0]
  if (post.value.images.length >= postImageNumLimit.value) {
    return alert(`アップロードできる画像は${postImageNumLimit.value}枚までです。`)
  }
  if (!allowedImageMimes.value.includes(image.type)) {
    const typeNames = allowedImageMimes.value.map(v => v.replace('image/', ''))
    return alert(t('pages.CompaniesStoreSnsPost.form.validation.invalidContentType', { types: typeNames.join(',') }))
  }
  if (image.size > imageSizeLimit.value) {
    return alert(t('pages.CompaniesStoreSnsPost.form.validation.overMediaSize'))
  }
  const { width, height } = await getImageSize(image)
  const aspectRatio = height / width
  if(allowedImageAspectRatio.value && (aspectRatio < allowedImageAspectRatio.value.min || aspectRatio > allowedImageAspectRatio.value.max)) {
    return alert(t('pages.CompaniesStoreSnsPost.form.validation.invalidImageAspectRatio'))
  }
  let payload = {
    url: '',
    width: '',
    height: '',
    filename: '',
    content: '',
    size: image.size,
    type: image.type,
  }
  payload.filename = image.name
  payload.url = URL.createObjectURL(image)
  payload.width = width
  payload.height = height

  const reader = new FileReader()

  reader.readAsDataURL(image)

  reader.onload = async () => {
    payload.content = reader.result
    updateImages(payload)
  }
}

const handleMovieChange = async($event) => {
  const movie = $event.target.files[0]
  if (!allowedMovieMimes.value.includes(movie.type)) {
    const typeNames = allowedMovieMimes.value.map(v => v.replace('video/', ''))
    return alert(t(`pages.CompaniesStoreSnsPost.form.validation.${typeNames.length === 0 ? 'notUploadMovie' : 'invalidContentType'}`, { types: typeNames.join(',') }))
  }
  if (movie.size > movieSizeLimit.value) {
    return alert(t('pages.CompaniesStoreSnsPost.form.validation.overMediaSize'))
  }
  let payload = {
    url: '',
    filename: '',
    content: '',
    time: '',
    size: movie.size,
    type: movie.type,
  }
  payload.filename = movie.name
  payload.url = URL.createObjectURL(movie)
  const { width, height } = await getMovieSize(payload.url)
  if (selectAccountSns.value.includes('INSTAGRAM') && width > INSTAGRAM_MOVIE_MAX_WIDTH) {
    return alert(t('pages.CompaniesStoreSnsPost.form.validation.invalidMovieWidth'))
  }
  payload.width = width
  payload.height = height
  const aspectRatio = width / height
  if(allowedMovieAspectRatio.value && (aspectRatio < allowedMovieAspectRatio.value.min || aspectRatio > allowedMovieAspectRatio.value.max)) {
    return alert(t('pages.CompaniesStoreSnsPost.form.validation.invalidMovieAspectRatio'))
  }
  const movieTime = await getMovieTime(payload.url)
  if (!allowedMovieTime(movieTime)) {
    return alert(t('pages.CompaniesStoreSnsPost.form.validation.invalidMovieTime'))
  }
  payload.time = displayedMovieTime(movieTime)
  const reader = new FileReader()

  reader.readAsDataURL(movie)

  reader.onload = async () => {
    payload.content = reader.result
    updateMovie(payload)
  }
}

const validMediaErrorTexts = computed(() => {
  const errors = []
  if (post.value.images.length > postImageNumLimit.value) {
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.limitImageCount', { num: postImageNumLimit.value }))
  }
  const types = post.value.images.map(v => v.type).filter(v => !!v)
  if (post.value.images.length > 0 && types.some(v => !allowedImageMimes.value.includes(v))) {
    const typeNames = allowedImageMimes.value.map(v => v.replace('image/', ''))
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.invalidContentType', { types: typeNames.join(',') }))
  }
  if (post.value.images.length > 0 && Math.max(...post.value.images.map(v => v.size)) > imageSizeLimit.value) {
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.overMediaSize'))
  }
  const imagesAspectRatio = post.value.images.map(v => v.height / v.width)
  if(allowedImageAspectRatio.value && imagesAspectRatio.some(v => v < allowedImageAspectRatio.value.min || v > allowedImageAspectRatio.value.max)) {
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.invalidImageAspectRatio'))
  }
  if (selectAccountSns.value.includes('INSTAGRAM') && post.value.images.length === 0 && !post.value.movie) {
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.requiredMediaUpload'))
  }
  if (!selectAccountSns.value.includes('INSTAGRAM') && !text.value && post.value.images.length === 0 && !post.value.movie) {
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.requiredTextOrMedia'))
  }
  if(!post.value.movie) return errors.join(', ')

  if(selectAccountSns.value.includes('FACEBOOK')) {
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.notUploadMovie'))
    return errors.join(', ')
  }

  if (!allowedMovieMimes.value.includes(post.value.movie.type)) {
    const typeNames = allowedMovieMimes.value.map(v => v.replace('video/', ''))
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.invalidContentType', { types: typeNames.join(',') }))
  }
  if (post.value.movie.size > movieSizeLimit.value) {
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.overMediaSize'))
  }
  const movieAspectRatio = post.value.movie.width / post.value.movie.height
  if(allowedMovieAspectRatio.value && (movieAspectRatio < allowedMovieAspectRatio.value.min || movieAspectRatio > allowedMovieAspectRatio.value.max)) {
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.invalidMovieAspectRatio'))
  }
  if (selectAccountSns.value.includes('INSTAGRAM') && post.value.movie.width > INSTAGRAM_MOVIE_MAX_WIDTH) {
    errors.push(t('pages.CompaniesStoreSnsPost.form.validation.invalidMovieWidth'))
  }
  return errors.join(', ')
})

const getMovieTime = (url) => {
  const video = document.createElement('video')
  video.src = url
  return new Promise((resolve) => {
    video.oncanplay = () => { resolve(video.duration) }
  })
}

const getMovieSize = (url) => {
  const video = document.createElement('video')
  video.src = url
  return new Promise((resolve) => {
    video.oncanplay = () => { resolve({ width: video.videoWidth, height: video.videoHeight }) }
  })
}

const displayedMovieTime = (time) => {
  const minutes = ('00' + Math.floor(time / 60)).slice(-2)
  const seconds = ('00' + Math.floor(time % 60)).slice(-2)
  return `${minutes}:${seconds}`
}

const getImageSize = async (data) => {
  return new Promise(resolve => {
    const image = new Image()
    const reader = new FileReader()
    reader.readAsDataURL(data)

    reader.onload = () => {
      image.src = reader.result

      image.onload = () => {
        resolve({ width: image.naturalWidth, height: image.naturalHeight })
      }
    }
  })
}

const openAccountsModal = () => {
  emit('open-accounts-modal')
}

const cancelAlert = () => {
  if (confirm(t('labels.cancelInput'))) {
    router.push({ path: '/s/companies/' + route.params.company_uuid + '/sns/posts' })
  }
}

const displayForm = computed(() => {
  return selectedPostSnsType.value !== 'MULTI' || isSelectedAccounts.value
})

const load = () => {
  readSnsCategories()
}

load()
</script>
<template>
  <form class="c-grid__items" @submit.prevent>
    <section class="main-box c-grid__item c-grid__item__s__12 c-grid__item__m__7 c-grid__item__l__7 settings">
      <KkBox border>
        <template #content>
          <div class="title">
            <img alt="" :src="snsImage" class="accountContainer__icon"><h3>{{ postTitle }}</h3>
          </div>
          <KkBox border>
            <template #content>
              <div class="form">
                <KkFormLabel :text="`${ t('pages.CompaniesStoreSnsPost.form.postTitle') }(${ $t('labels.countCharacters', { count: title.length, max: 40 }) })${ $t('labels.requiredLabel', { count: title.length }) }`" />
                <KkTextField :model-value="title" class="c-textField" :placeholder="$t('messages.noSettings')" @update:model-value="titleHandleChange" />
                <KkValidationError :text="titleError" />
              </div>
              <div class="form">
                <KkFormLabel :text="`${ t('pages.CompaniesStoreSnsPost.form.selectCategory') }${ t('labels.optionalLabel') }`" />
                <KkSelect
                  :options="categoryOptions()"
                  :model-value="categoryId"
                  @update:model-value="categoryIdHandleChange"
                />
                <KkValidationError :text="categoryIdError" />
              </div>
              <KkInfo icon="hint">
                {{ $t('pages.CompaniesStoreSnsPost.form.titleInfo') }}
              </KkInfo>
            </template>
          </KkBox>
          <KkBox border>
            <template #content>
              <KkSelectAccounts
                @open-accounts-modal="openAccountsModal"
              />
            </template>
          </KkBox>
        </template>
      </KkBox>
      <div v-if="displayForm">
        <div class="form">
          <KkFormLabel :text="`${ t('pages.CompaniesStoreSnsPost.form.addMedia') }（${ post.images.length }/${postImageNumLimit}${ t('components.PostImage.unit') }）（${ mediaNote }）`" />
          <KkPostMedia
            :disabled="mediaDisabled"
            @update:image-value="handleImagesChange"
            @update:movie-value="handleMovieChange"
          />
          <KkValidationError :text="validMediaErrorTexts" />
        </div>
        <div class="form">
          <KkFormLabel :text="`${ t('pages.CompaniesStoreSnsPost.form.enterText') } (${ $t('labels.countCharacters', { count: text.length, max: textCharacterLimit.toLocaleString() }) })`" />
          <KkTextarea :model-value="text" placeholder="" @update:model-value="textHandleChange" />
          <KkValidationError :text="textError" />
        </div>
      </div>
    </section>
    <section class="sub-box c-grid__item c-grid__item__s__12 c-grid__item__m__5 c-grid__item__l__5 preview-box">
      <KkPostSetting
        :need-approval="props.needApproval"
        :valid="meta.valid && !validMediaErrorTexts"
        @open-action-modal="openActionModal"
        @cancel-alert="cancelAlert"
      />
      <KkFacebookPreview v-if="showFacebookPreview" class="preview" :text="text" :images="post.images" :movie="post.movie" />
      <KkInstagramPreview v-if="showInstagramPreview" class="preview" :text="text" :images="post.images" :movie="post.movie" />
      <KkTwitterPreview v-if="showTwitterPreview" class="preview" :text="text" :images="post.images" :movie="post.movie" />
    </section>
  </form>
</template>

<style lang="scss" scoped>
.title {
  display: flex;
  img {
    margin-bottom: 16px;
    margin-right: 8px;
  }
}
.form {
  margin-bottom: 16px;
  &:first-child {
    margin-bottom: 30px;
  }
}
.box {
  margin-bottom: 30px;
}
.preview {
  margin-bottom: 30px;
}
</style>
